import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import PropTypes from 'prop-types';

import iconSet from '../../../shared/images/teambuildr-selection.json';
import optimizeImage from '../../../shared/utils/imageOptimizer';

import { setTvMode } from '../ducks/leaderboardActions';

const MainHeader = styled('header')`
  background-color: ${(props) => props.theme.colorPrimary};
  display: flex;
  position: fixed;
  z-index: 1000;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 49px;
  padding: 0 15px;
  box-sizing: border-box;
  animation: fadeIn 250ms ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const Logo = styled('a', {
  shouldForwardProp: (prop) => prop !== 'logo',
})`
  background-image: ${(props) => `url(${props.logo})`};
  background-position: left 50%;
  background-size: contain;
  background-repeat: no-repeat;
  min-width: 35px;
  width: 65px;
  height: 35px;
  cursor: pointer;
`;

const RightSection = styled('div')`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const IconButton = styled('button')`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 25px;
  width: 25px;
  border: 0;
  background: transparent;
  cursor: pointer;
`;

const LeaderboardTvHeader = ({ isPrimaryDarkTheme, logo }) => {
  const isAccountAdmin = useSelector((state) => state.auth.data.currentUser?.admin) === 1;
  const dispatch = useDispatch();
  const [isFullscreen, setIsFullscreen] = useState(!!document.fullscreenElement);
  const optimizedLogo = useMemo(() => optimizeImage(logo, {
    resize: {
      minWidth: 40,
      maxWidth: 80,
      minHeight: 40,
      maxHeight: 80,
      fit: 'contain',
    },
  }), [logo]);

  const homePath = isAccountAdmin ? '/dashboard' : '/workouts';
  // iPhone (WebKit-only) has no Fullscreen API for regular elements — iPad,
  // Android, and desktop all support it. Toggle only renders where supported;
  // low-stakes since TV Mode is unlikely on mobile anyway.
  const canFullscreen = !!document.fullscreenEnabled;

  const handleExitTvMode = () => {
    dispatch(setTvMode(false));
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    dispatch(push(homePath));
  };

  const handleToggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else if (document.documentElement.requestFullscreen) {
      document.documentElement.requestFullscreen().catch(() => {});
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  return (
    <MainHeader>
      <Logo href={homePath} onClick={handleLogoClick} id='logo' title='logo' logo={optimizedLogo} />
      <RightSection>
        {canFullscreen && (
          <IconButton
            onClick={handleToggleFullscreen}
            aria-label={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
          >
            <IcomoonReact
              iconSet={iconSet}
              size={14}
              icon={isFullscreen ? 'collapse' : 'expand2'}
              color={isPrimaryDarkTheme ? '#FFFFFF' : '#444444'}
            />
          </IconButton>
        )}
        <IconButton onClick={handleExitTvMode} aria-label='Exit TV Mode'>
          <IcomoonReact
            iconSet={iconSet}
            size={12}
            icon='remove'
            color={isPrimaryDarkTheme ? '#FFFFFF' : '#444444'}
          />
        </IconButton>
      </RightSection>
    </MainHeader>
  );
};

LeaderboardTvHeader.propTypes = {
  isPrimaryDarkTheme: PropTypes.bool,
  logo: PropTypes.string,
};

LeaderboardTvHeader.defaultProps = {
  isPrimaryDarkTheme: false,
  logo: '',
};

export default LeaderboardTvHeader;
